import { Suspense, lazy } from "react";
import { LazyMotion, domAnimation } from "framer-motion";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/hooks/useAuth";
import { AdminRoute } from "@/components/AdminRoute";
import PageLoader from "@/components/ui/PageLoader";
import PageTransition from "@/components/ui/PageTransition";
import { captureAcquisitionContext } from "@/lib/formSubmit";

// Eager load - critical path (home page)
import Index from "./pages/Index";

// Lazy load - secondary pages
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Careers = lazy(() => import("./pages/Careers"));
const Partners = lazy(() => import("./pages/Partners"));
const Afamekin2 = lazy(() => import("./pages/Afamekin2"));


const Methodology = lazy(() => import("./pages/Methodology"));
const Compliance = lazy(() => import("./pages/Compliance"));
const References = lazy(() => import("./pages/References"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Tenders = lazy(() => import("./pages/Tenders"));
const Contact = lazy(() => import("./pages/Contact"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const FAQ = lazy(() => import("./pages/FAQ"));
const QuoteRequest = lazy(() => import("./pages/QuoteRequest"));
const News = lazy(() => import("./pages/News"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const Actualites = lazy(() => import("./pages/Actualites"));
const Manifest = lazy(() => import("./pages/Manifest"));
const Appointment = lazy(() => import("./pages/Appointment"));
const Documents = lazy(() => import("./pages/Documents"));
const Branding = lazy(() => import("./pages/Branding"));

// Lazy load - admin pages (rarely accessed)
const Admin = lazy(() => import("./pages/Admin"));
const ChantierMobile = lazy(() => import("./pages/ChantierMobile"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const SeoValidation = lazy(() => import("./pages/SeoValidation"));
const Install = lazy(() => import("./pages/Install"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const queryClient = new QueryClient();

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <PageTransition key={location.pathname}>
      <Routes location={location}>
        <Route path="/" element={<Index />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/afamekin2" element={<Afamekin2 />} />

        <Route path="/methodology" element={<Methodology />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/references" element={<References />} />
        <Route path="/references/:slug" element={<ProjectDetail />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/tenders" element={<Tenders />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/legal-notice" element={<LegalNotice />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/quote-request" element={<QuoteRequest />} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:slug" element={<NewsArticle />} />
        <Route path="/actualites" element={<Actualites />} />
        <Route path="/manifest" element={<AdminRoute><Manifest /></AdminRoute>} />
        <Route path="/appointment" element={<Appointment />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/branding" element={<AdminRoute><Branding /></AdminRoute>} />
        <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
        <Route path="/chantier-mobile" element={<AdminRoute><ChantierMobile /></AdminRoute>} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/seo-validation" element={<AdminRoute><SeoValidation /></AdminRoute>} />
        <Route path="/install" element={<Install />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </PageTransition>
  );
};

// Capture la provenance (UTM, page d'arrivée) une seule fois par session,
// afin de la rattacher aux demandes envoyées depuis les formulaires.
captureAcquisitionContext();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <AuthProvider>
        <TooltipProvider>
          <LazyMotion features={domAnimation} strict>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <AnimatedRoutes />
              </Suspense>
            </BrowserRouter>
          </LazyMotion>
        </TooltipProvider>
      </AuthProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
